import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnswerRefScope } from './answer-ref-context';

import type { BlockIssue } from './block-issues';

import { useCapabilities } from '@/hooks/use-capabilities';
import { recallRungIssues } from '@/lib/recall-issues';

/**
 * A RECALL IN A BLOCK WHOSE RUNG DOES NOT SUPPORT ONE, reported on the block that holds it.
 *
 * The sibling of `useOutOfRungVariableIssues`, and here for the same two reasons. No block can
 * compute it — a block knows what it does with a recall, not whether the teacher's level allows
 * one — and the BOARD alone is not enough, because an editor computes its own issues and never
 * goes through the dispatcher. A check on one surface reads as no check at all from the other.
 *
 * Its inputs come from the answer-ref scope, which is already mounted around every editor, so
 * this needs no prop and no block file changes.
 */
export function useOutOfRungRecallIssues(blockId: string | undefined): BlockIssue[] {
  const { t } = useTranslation();
  const scope = useAnswerRefScope();
  const { level } = useCapabilities();
  const rung = level('answerRecall');

  const tasks = scope?.tasks;
  const aliases = scope?.aliases;
  const currentTaskId = scope?.currentTaskId;
  return useMemo(() => {
    if (!blockId || !tasks || !currentTaskId) return [];
    /* Searched across every task, not just the current one: a summary-feedback block is mounted
       outside the task it belongs to, and a wrong "not found" here fails silent. */
    const block = tasks.flatMap(task => task.blocks ?? []).find(b => b.id === blockId);
    if (!block) return [];
    return recallRungIssues(block, {
      tasks: tasks as never, aliases: aliases ?? [], currentTaskId, t, rung,
    });
  }, [blockId, tasks, aliases, currentTaskId, rung, t]);
}
